import { Icon } from "@iconify/react";
import type { ComponentProps } from "react";
import { Link } from "react-router-dom";
import type { NotificationDto } from "@/api/notifications";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import {
  formatNotificationTime,
  localizedNotificationCategoryLabel,
  localizedNotificationTitle,
  notificationActionRoute,
  notificationPresentation,
} from "./notification-presentation";

type NotificationListItemProps = {
  className?: ComponentProps<"div">["className"];
  notification: NotificationDto;
  onSelect: (notification: NotificationDto) => void;
};

export function NotificationListItem({ className, notification, onSelect }: NotificationListItemProps) {
  const { intlLocale, t } = useI18n();
  const presentation = notificationPresentation(notification);
  const route = notificationActionRoute(notification);
  const unread = notification.readTime === null;

  return (
    <div className={cn("flex w-full min-w-0 max-w-full items-start gap-2.5 overflow-hidden rounded-lg px-2 py-2.5 text-left", className)}>
      <span className={cn("mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-muted/60", presentation.iconClassName)}>
        <Icon className="size-3.5" icon={presentation.icon} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block max-w-full truncate text-[13px] leading-5 text-foreground", unread ? "font-semibold" : "font-medium")}>
          {localizedNotificationTitle(notification, t)}
        </span>
        {notification.body ? (
          <span className="mt-0.5 line-clamp-2 max-h-8 wrap-break-word text-xs leading-4 text-muted-foreground">{notification.body}</span>
        ) : null}
        <span className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          <span>{formatNotificationTime(notification.creationTime, intlLocale)}</span>
          <span aria-hidden="true" className="size-1 rounded-full bg-border" />
          <span>{localizedNotificationCategoryLabel(notification, t)}</span>
          {route ? (
            <>
              <span aria-hidden="true" className="size-1 rounded-full bg-border" />
              <Link className="text-primary underline-offset-2 hover:underline focus-visible:underline" to={route} onClick={() => onSelect(notification)}>
                {t("notifications.viewDetails")}
              </Link>
            </>
          ) : null}
        </span>
      </span>
      {unread ? <span aria-hidden="true" className="mt-1.5 size-2 shrink-0 rounded-sm bg-primary" /> : null}
    </div>
  );
}
